import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  UserPlus,
  Image as ImageIcon,
  Mail,
  Network,
  Check,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  HelpCircle,
} from 'lucide-react';

export type OnboardingStepKey = 'added' | 'uploaded' | 'invited';

interface OnboardingTourProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Map of completed step keys -> true. */
  completed: Record<OnboardingStepKey, boolean>;
  /** Mark a step as complete. */
  markComplete: (key: OnboardingStepKey) => void;
  /** Navigate to the "add family member" experience. */
  onAddMember: () => void;
  /** Navigate to the upload/memories experience. */
  onUploadMemory: () => void;
  /** Open the invite-family modal. */
  onInviteFamily: () => void;
  /** Navigate to the family tree view. */
  onExploreTree: () => void;
}

interface StepDef {
  key: 'added' | 'uploaded' | 'invited' | 'tree';
  number: number;
  title: string;
  body: string;
  detail?: string;
  cta: string;
  Icon: React.ComponentType<{ className?: string }>;
  toneFrom: string;
  toneTo: string;
  trackable: boolean; // counts toward the checklist
}

const STEPS: StepDef[] = [
  {
    key: 'added',
    number: 1,
    title: 'Add yourself or a family member',
    body: 'Start your circle by adding yourself, a parent, sibling, or any loved one.',
    detail: 'Tap the button below to open the family tree and add your first person.',
    cta: 'Add a Family Member',
    Icon: UserPlus,
    toneFrom: 'from-fuchsia-500',
    toneTo: 'to-pink-500',
    trackable: true,
  },
  {
    key: 'uploaded',
    number: 2,
    title: 'Upload your first memory',
    body: 'Add a photo or video so the story comes alive.',
    detail:
      "You can add photos and videos to your own profile, or to any family member's timeline. Open a person, tap \"Add Photo or Video\", and it will appear in their history.",
    cta: 'Upload a Memory',

    Icon: ImageIcon,
    toneFrom: 'from-amber-500',
    toneTo: 'to-orange-500',
    trackable: true,
  },
  {
    key: 'invited',
    number: 3,
    title: 'Invite your family',
    body: 'KinsCircle is better together. Invite a parent, sibling, or cousin to join you.',
    detail: "They'll get a private link to join your family circle.",

    cta: 'Invite Family',
    Icon: Mail,
    toneFrom: 'from-emerald-500',
    toneTo: 'to-teal-500',
    trackable: true,
  },
  {
    key: 'tree',
    number: 4,
    title: 'Explore your family tree',
    body: 'See how everyone connects — parents, kids, cousins, and more.',

    detail: 'Tap any person to view their profile, photos, and stories.',
    cta: 'Open Family Tree',
    Icon: Network,
    toneFrom: 'from-purple-500',
    toneTo: 'to-fuchsia-600',
    trackable: false,
  },
];

const OnboardingTour: React.FC<OnboardingTourProps> = ({
  open,
  onOpenChange,
  completed,
  markComplete,
  onAddMember,
  onUploadMemory,
  onInviteFamily,
  onExploreTree,
}) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [showGuide, setShowGuide] = useState(false);

  // Reset to first step every time the dialog opens.
  useEffect(() => {
    if (open) {
      setStepIdx(0);
      setShowGuide(false);
    }
  }, [open]);

  const step = STEPS[stepIdx];
  const totalTrackable = STEPS.filter((s) => s.trackable).length;
  const completedCount = useMemo(
    () =>
      (completed.added ? 1 : 0) +
      (completed.uploaded ? 1 : 0) +
      (completed.invited ? 1 : 0),
    [completed],
  );

  const isStepComplete = (key: StepDef['key']) =>
    key === 'tree' ? false : !!completed[key as OnboardingStepKey];

  const handleAction = () => {
    // Mark trackable steps complete when the user takes the suggested action,
    // close the tour, and route them to the right surface.
    if (step.key === 'added') {
      markComplete('added');
      onOpenChange(false);
      // Defer navigation so the dialog can fully close before scrolling.
      window.setTimeout(() => onAddMember(), 80);
      return;
    }
    if (step.key === 'uploaded') {
      markComplete('uploaded');
      onOpenChange(false);
      window.setTimeout(() => onUploadMemory(), 80);
      return;
    }
    if (step.key === 'invited') {
      markComplete('invited');
      onOpenChange(false);
      window.setTimeout(() => onInviteFamily(), 80);
      return;
    }
    if (step.key === 'tree') {
      onOpenChange(false);
      window.setTimeout(() => onExploreTree(), 80);
      return;
    }
  };

  const goNext = () => {
    if (stepIdx < STEPS.length - 1) setStepIdx((i) => i + 1);
  };
  const goPrev = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  const Icon = step.Icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden rounded-2xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Visually-hidden a11y title/description so Radix can announce
            the dialog to screen readers without changing the visible UI. */}
        <DialogTitle className="sr-only">Welcome to KinsCircle</DialogTitle>
        <DialogDescription className="sr-only">
          A quick four-step guide to help you start your family circle: add a
          family member, upload a memory, invite your family, and explore your
          family tree.
        </DialogDescription>
        {/* Header */}
        <div className="bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 px-6 pt-6 pb-4 border-b border-fuchsia-100">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 border border-fuchsia-200 text-fuchsia-700 text-[11px] font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Welcome to KinsCircle
            </div>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="p-1.5 rounded-full hover:bg-white/70 text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h2 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
            Let's build your family story
          </h2>

          <p className="text-sm text-slate-600 mt-1">
            Four tiny steps. Big memories. You can come back to this guide anytime.
          </p>

          {/* Percentage started */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] font-semibold mb-1.5">
              <span className="text-slate-600">
                Your family is{' '}
                <span className="text-fuchsia-700">
                  {Math.round((completedCount / totalTrackable) * 100)}%
                </span>{' '}
                started
              </span>
              <span className="text-slate-500">
                {completedCount} / {totalTrackable}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 transition-all"
                style={{ width: `${(completedCount / totalTrackable) * 100}%` }}
              />
            </div>
          </div>

          {/* Step pips */}
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((s, i) => {
              const done = isStepComplete(s.key);
              const active = i === stepIdx;
              return (
                <button
                  key={s.key}
                  onClick={() => setStepIdx(i)}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    done
                      ? 'bg-emerald-500'
                      : active
                      ? 'bg-fuchsia-500'
                      : 'bg-slate-200'
                  }`}
                  aria-label={`Go to step ${s.number}`}
                />
              );
            })}
          </div>
        </div>


        {/* Step body */}
        {!showGuide && (
          <div className="px-6 py-6">
            <div className="flex items-start gap-4">
              <div
                className={`shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${step.toneFrom} ${step.toneTo} flex items-center justify-center shadow-md`}
              >
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  Step {step.number} of {STEPS.length}
                </div>
                <h3 className="font-serif text-lg font-bold text-slate-900 mt-0.5">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-700 mt-1.5">{step.body}</p>
                {step.detail && (
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                    {step.detail}
                  </p>
                )}
              </div>
            </div>

            {/* Primary CTA */}
            <Button
              onClick={handleAction}
              className="w-full mt-5 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white py-5 text-base font-semibold shadow-md gap-2"
            >
              {step.cta}
              <ArrowRight className="w-4 h-4" />
            </Button>

            {/* Step nav */}
            <div className="flex items-center justify-between mt-4">
              <Button
                variant="ghost"
                onClick={goPrev}
                disabled={stepIdx === 0}
                className="rounded-full text-slate-600 gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <button
                onClick={() => setShowGuide(true)}
                className="text-xs font-semibold text-fuchsia-700 hover:text-fuchsia-800 inline-flex items-center gap-1"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                More help
              </button>
              {stepIdx < STEPS.length - 1 ? (
                <Button
                  variant="ghost"
                  onClick={goNext}
                  className="rounded-full text-slate-700 gap-1"
                >
                  Skip
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  className="rounded-full text-slate-700"
                >
                  Done
                </Button>
              )}
            </div>

            {/* Mini progress summary */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                  Your progress
                </div>
                <div className="text-xs font-semibold text-slate-700">
                  {completedCount} of {totalTrackable} done
                </div>
              </div>
              <ul className="space-y-1.5">
                {STEPS.filter((s) => s.trackable).map((s) => {
                  const done = !!completed[s.key as OnboardingStepKey];
                  return (
                    <li key={s.key} className="flex items-center gap-2 text-xs">
                      <span
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          done
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-400'
                        }`}
                      >
                        {done && <Check className="w-3 h-3" />}
                      </span>
                      <span
                        className={
                          done ? 'text-slate-500 line-through' : 'text-slate-700'
                        }
                      >
                        {s.title}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Extended guide */}
        {showGuide && (
          <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-lg font-bold text-slate-900 inline-flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-fuchsia-600" />
                How to use KinsCircle
              </h3>
              <button
                onClick={() => setShowGuide(false)}
                className="text-xs font-semibold text-fuchsia-700 hover:text-fuchsia-800"
              >
                Back to steps
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Tap a topic to learn more. None of this is required — just here when you need it.
            </p>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="profile">
                <AccordionTrigger className="text-sm font-semibold">
                  Editing your profile
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">
                  Tap your name or photo on the family tree to open your
                  profile. From there you can update your name, photo, and
                  other details. Changes save automatically.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="nicknames">
                <AccordionTrigger className="text-sm font-semibold">
                  Adding nicknames
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">
                  Open a person's profile and edit their details. You can
                  add a nickname like "Grandma Rose" so it shows up in the
                  tree and on memories.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="viewing">
                <AccordionTrigger className="text-sm font-semibold">
                  Viewing photos and videos
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">
                  Open the Memories section, or tap any person to see only
                  their photos and videos. Tap a photo to view it full size.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="timeline">
                <AccordionTrigger className="text-sm font-semibold">
                  Adding memories to a person's timeline
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">
                  Open a person's profile, tap "Add Photo or Video", pick
                  a file, and it will appear in their history. You can add a
                  caption and a date so the memory is easy to find later.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="connections">
                <AccordionTrigger className="text-sm font-semibold">
                  Understanding family connections
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">
                  Each person is linked by a relationship — parent,
                  sibling, child, spouse, or cousin. KinsCircle uses these
                  links to draw your family tree. You can edit a person and
                  change their relationship at any time.
                </AccordionContent>
              </AccordionItem>


              <AccordionItem value="privacy">
                <AccordionTrigger className="text-sm font-semibold">
                  Privacy and who can see your content
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600">
                  Your family circle is private by default. Only people you
                  invite can see your memories. You can review who has joined
                  in the Family Tree, and remove anyone from the Account
                  page.
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Button
              onClick={() => setShowGuide(false)}
              variant="outline"
              className="w-full mt-5 rounded-full"
            >
              Back to steps
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTour;
